import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaBell, 
  FaCalendarAlt, 
  FaSpinner,
  FaInbox,
  FaCheckCircle,
  FaEnvelope,
  FaEnvelopeOpen,
  FaCheck
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import './EmployeeNotices.css';
import Layout from '../layouts/Layout';

const EmployeeNotices = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const baseUrl = "https://api.burrayhr.com";

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/notices/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log("Notices", response.data);
      setNotices(response.data);
    } catch (error) {
      console.error('Error fetching notices:', error);
      showAlert('error', 'Failed to fetch notices');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    Swal.fire({
      icon: type,
      title: type === 'success' ? 'Success!' : 'Error!',
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: type === 'success' ? '#d4edda' : '#f8d7da',
      color: type === 'success' ? '#155724' : '#721c24',
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleNoticeClick = async (notice) => {
    const result = await Swal.fire({
      title: notice.title,
      html: `
        <div class="notice-alert-content">
          <div class="notice-alert-meta">
            <span><i class="fa fa-calendar"></i> Posted on ${formatDateTime(notice.created_at)}</span>
            ${notice.is_read_by_current_user ? '<span><i class="fa fa-check-circle"></i> Read</span>' : ''}
          </div>
          <div style="left: 0; padding: 20px; padding-right: 5px; text-align: center;" class="notice-alert-body">
            ${notice.content}
          </div>
          ${!notice.is_read_by_current_user ? `
            <div style="margin-top: 20px; text-align: center;">
              <button id="markAsReadBtn" class="swal2-confirm swal2-styled" style="background-color: #1e3a8a;">
                Mark as Read
              </button>
            </div>
          ` : ''}
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: () => {
        const markAsReadBtn = document.getElementById('markAsReadBtn');
        if (markAsReadBtn) {
          markAsReadBtn.addEventListener('click', async () => {
            Swal.close();
            await showMarkAsReadModal(notice);
          });
        }
      }
    });
  };

  const showMarkAsReadModal = async (notice) => {
    const { value: message } = await Swal.fire({
      title: 'Mark Notice as Read',
      html: `
        <div class="mark-as-read-modal" style="
          width: 100%;
          overflow-x: hidden;
          padding: 0 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
        ">
          <div style="
            background-color: #f8fafc;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            border: 1px solid #e2e8f0;
            width: 100%;
            max-width: 400px;
            text-align: center;
          ">
            <p style="
              margin-bottom: 15px;
              color: #1e293b;
              font-size: 0.95rem;
              font-weight: 500;
              text-align: center;
            ">Add an optional message (leave blank to skip):</p>
            <textarea 
              id="readMessage" 
              class="swal2-textarea" 
              placeholder="Enter an optional message here..."
              style="
                width: 100%;
                min-height: 120px;
                margin-bottom: 15px;
                padding: 12px;
                border: 1px solid #e2e8f0;
                border-radius: 6px;
                font-size: 0.95rem;
                line-height: 1.5;
                resize: vertical;
                background-color: white;
                transition: border-color 0.2s ease;
                font-family: inherit;
                display: block;
                margin-left: auto;
                margin-right: auto;
              "
            ></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Mark as Read',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#1e3a8a',
      cancelButtonColor: '#dc2626',
      showLoaderOnConfirm: true,
      customClass: {
        container: 'mark-as-read-modal-container',
        popup: 'mark-as-read-modal-popup',
        title: 'mark-as-read-modal-title',
        htmlContainer: 'mark-as-read-modal-content',
        confirmButton: 'mark-as-read-modal-confirm-btn',
        cancelButton: 'mark-as-read-modal-cancel-btn',
        actions: 'mark-as-read-modal-actions'
      },
      preConfirm: async () => {
        const messageInput = document.getElementById('readMessage');
        const message = messageInput.value.trim();
        
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${baseUrl}/api/notices/mark_as_read/`,
            {
              notice_id: notice.id,
              message: message || null // Send null if message is empty
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          // Update the notice in the state
          setNotices(prevNotices => 
            prevNotices.map(n => 
              n.id === notice.id 
                ? { ...n, is_read_by_current_user: true }
                : n
            )
          );

          return true;
        } catch (error) {
          console.error('Error marking notice as read:', error);
          Swal.showValidationMessage('Failed to mark notice as read');
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: () => {
        const style = document.createElement('style');
        style.textContent = `
          .mark-as-read-modal-container {
            overflow-x: hidden !important;
          }
          .mark-as-read-modal-popup {
            max-width: 500px !important;
            width: 90% !important;
            padding: 1.5rem !important;
            border-radius: 12px !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
          }
          .mark-as-read-modal-title {
            color: #1e3a8a !important;
            font-size: 1.25rem !important;
            font-weight: 600 !important;
            margin-bottom: 1rem !important;
            padding-bottom: 0.5rem !important;
            border-bottom: 1px solid #e2e8f0 !important;
            text-align: center !important;
            width: 100% !important;
          }
          .mark-as-read-modal-content {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
          }
          .mark-as-read-modal-actions {
            display: flex !important;
            justify-content: center !important;
            gap: 1rem !important;
            margin-top: 1rem !important;
            width: 100% !important;
          }
          .mark-as-read-modal-confirm-btn,
          .mark-as-read-modal-cancel-btn {
            padding: 0.5rem 1.5rem !important;
            font-weight: 500 !important;
            border-radius: 6px !important;
            transition: all 0.2s ease !important;
            min-width: 100px !important;
          }
          .mark-as-read-modal-confirm-btn:hover,
          .mark-as-read-modal-cancel-btn:hover {
            transform: translateY(-1px) !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
          }
          .swal2-textarea {
            text-align: center !important;
            margin: 0 auto !important;
          }
          .swal2-textarea:focus {
            border-color: #1e3a8a !important;
            box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1) !important;
            outline: none !important;
          }
          .swal2-validation-message {
            background-color: #fee2e2 !important;
            color: #dc2626 !important;
            border-radius: 6px !important;
            padding: 0.75rem !important;
            margin-top: 1rem !important;
            font-size: 0.875rem !important;
            text-align: center !important;
            width: 100% !important;
          }
        `;
        document.head.appendChild(style);
      }
    });

    if (message !== undefined) {
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Notice marked as read successfully',
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          popup: 'success-modal-popup',
          title: 'success-modal-title',
          content: 'success-modal-content'
        },
        didOpen: () => {
          const style = document.createElement('style');
          style.textContent = `
            .success-modal-popup {
              border-radius: 12px !important;
              padding: 1.5rem !important;
            }
            .success-modal-title {
              color: #059669 !important;
              font-size: 1.25rem !important;
              font-weight: 600 !important;
            }
            .success-modal-content {
              color: #374151 !important;
              font-size: 0.95rem !important;
            }
          `;
          document.head.appendChild(style);
        }
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="notices-container">
          <div className="notices-header">
            <FaSpinner className="header-icon fa-spin" />
            <h2>Loading notices...</h2>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="notices-container">
        <div className="notices-header">
          <FaBell className="header-icon" />
          <h2>Notices</h2>
          <div className="notices-stats">
            
          </div>
        </div>

        {notices.length === 0 ? (
          <div className="empty-state">
            <FaInbox className="empty-state-icon" />
            <h3 className="empty-state-text">No Notices</h3>
            <p className="empty-state-subtext">You have no notices at this time.</p>
          </div>
        ) : (
          <div className="notices-grid">
            {notices.map(notice => (
              <div 
                key={notice.id} 
                className={`notice-card ${!notice.is_read_by_current_user ? 'unread' : ''}`}
                onClick={() => handleNoticeClick(notice)}
              >
                {!notice.is_read_by_current_user && (
                  <div className="unread-indicator">
                    <span className="unread-dot"></span>
                    <span className="unread-label">New</span>
                  </div>
                )}
                <div className="notice-header">
                  <h3 className="notice-title">
                    {notice.is_read_by_current_user ? <FaEnvelopeOpen /> : <FaEnvelope />}
                    {notice.title}
                  </h3>
                </div>

                <div className="notice-body">
                  <p className="notice-content">{notice.content}</p>
                </div>

                <div className="notice-footer">
                  <span className="notice-date">
                    <FaCalendarAlt />
                    {formatDate(notice.created_at)}
                  </span>
                  <span className={`notice-status ${notice.is_read_by_current_user ? 'read' : 'unread'}`}>
                    {notice.is_read_by_current_user ? (
                      <>
                        <FaCheckCircle />
                        Read
                      </>
                    ) : (
                      <>
                        <FaBell />
                        Unread
                      </>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .notices-container {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .notices-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding: 1.5rem;
          background-color: white;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .notices-stats {
          margin-left: auto;
        }

        .unread-count {
          background-color: #fee2e2;
          color: #dc2626;
          padding: 0.5rem 1rem;
          border-radius: 2rem;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .notice-card {
          position: relative;
          background-color: white;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid #e5e7eb;
        }

        .notice-card.unread {
          background-color: #f8fafc;
          border-left: 4px solid #1e3a8a;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .notice-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .unread-indicator {
          position: absolute;
          top: 1rem;
          right: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .unread-dot {
          width: 8px;
          height: 8px;
          background-color: #dc2626;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .unread-label {
          background-color: #fee2e2;
          color: #dc2626;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .notice-header {
          margin-bottom: 1rem;
        }

        .notice-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #1f2937;
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0;
        }

        .notice-body {
          margin-bottom: 1rem;
        }

        .notice-content {
          color: #4b5563;
          font-size: 0.95rem;
          line-height: 1.5;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .notice-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .notice-date {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .notice-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
        }

        .notice-status.read {
          background-color: #f0fdf4;
          color: #059669;
        }

        .notice-status.unread {
          background-color: #eff6ff;
          color: #1e3a8a;
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.7;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          .notices-container {
            padding: 1rem;
          }

          .notices-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
            padding: 1rem;
          }

          .notices-stats {
            margin-left: 0;
            width: 100%;
          }

          .notice-card {
            padding: 1rem;
          }

          .notice-title {
            font-size: 1rem;
          }

          .notice-content {
            font-size: 0.875rem;
          }

          .notice-status {
            font-size: 0.75rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default EmployeeNotices;